import React from 'react';
import PropTypes from 'prop-types';

import styled from 'styled-components';
import { media } from '../../../styles/style-utils';
import liveFallback from '../../../assets/images/live-fallback.jpg';
import PriceTemplate from './PriceTemplate';
import AvailabilityInfo from './AvailabilityInfo';
import Hyperlink from '../../common/Hyperlink';
import { getArtworkUrl } from '../utils/Contentful';
import { withSiteData } from 'react-static';

const Container = styled.div`
  width: calc(18 * var(--fr)) !important;
  transform: scale(1);
  transition: transform 0.2s;

  ${media.mediumLarge`
    width: calc(32 * var(--fr)) !important;
  `};

  :hover {
    transform: scale(1.025);
  }
`;

const Artwork = styled.img`
  margin-bottom: calc(var(--fr) * 0.75);
`;

const Info = styled.div`
  flex-grow: 1;

  .location {
    margin-bottom: calc(var(--fr) / 2);
  }

  .price,
  .availability {
    margin-bottom: calc(var(--fr) * 0.75);
  }

  .price,
  .location,
  .availability {
    font-size: var(--fs-focus);
    font-weight: 400;
  }
`;

export const livestreamEventDecorator = content => {
  const present = new Date();
  const start = new Date(content.start);
  const end = new Date(content.end);
  const hasStarted = present > start;
  const isUpcoming = !hasStarted;

  const minuteInMilliseconds = 60 * 1000;
  const hourInMilliseconds = 60 * minuteInMilliseconds;

  const defaultAvailabilityEnd = new Date(end.getTime() + 24 * hourInMilliseconds);
  const availabilityEnd = content.availabilityEnd || defaultAvailabilityEnd;

  const isAvailable = new Date(availabilityEnd) - present > 0;

  // price
  const { ticketPriceEuro, ticketPriceGbp, ticketPriceUsd, priceTemplate, type } = content;

  const hasOldPriceSet = Boolean(
    ticketPriceEuro !== undefined || ticketPriceGbp !== undefined || ticketPriceUsd !== undefined
  );

  let priceTemplateFields;
  let hasPriceTemplateSet;

  if (priceTemplate) {
    priceTemplateFields = priceTemplate.fields;
    hasPriceTemplateSet = Boolean(priceTemplate && priceTemplateFields && priceTemplateFields.name);
  }

  const isConcert = type === 'concert';
  const shouldShowPrice = isConcert && (hasOldPriceSet || hasPriceTemplateSet);
  const isFreeConcert =
    (hasOldPriceSet && ticketPriceEuro === 0) ||
    (hasPriceTemplateSet && priceTemplateFields.minEur === 0);
  const isFixedPrice =
    (hasOldPriceSet && !hasPriceTemplateSet) ||
    (hasPriceTemplateSet && Boolean(priceTemplateFields.isFixedPrice));

  // Maintain backwards compatibility with old ticketPrice* keys
  let fixedValues = {
    EUR: ticketPriceEuro ? ticketPriceEuro : null,
    USD: ticketPriceUsd ? ticketPriceUsd : null,
    GBP: ticketPriceGbp ? ticketPriceGbp : null,
  };
  let variableValues = null;

  // Handle price templates
  if (hasPriceTemplateSet) {
    if (isFixedPrice) {
      // Overwrite fixed price
      fixedValues = {
        EUR: priceTemplateFields.minEur,
        GBP: priceTemplateFields.minGbp,
        USD: priceTemplateFields.minUsd,
      };
    } else {
      variableValues = {};
      variableValues.min = {
        EUR: priceTemplateFields.minEur,
        GBP: priceTemplateFields.minGbp,
        USD: priceTemplateFields.minUsd,
      };
      variableValues.max = {
        EUR: priceTemplateFields.maxEur,
        GBP: priceTemplateFields.maxGbp,
        USD: priceTemplateFields.maxUsd,
      };
    }
  }

  return {
    startDate: start,
    endDate: end,
    isUpcoming,
    isAvailable,
    availabilityEnd,

    // price
    shouldShowPrice,
    isFree: isFreeConcert,
    isFixed: isFixedPrice,
    fixedValues,
    variableValues,
  };
};

const LivestreamEvent = ({ content, appDomain }) => {
  const event = livestreamEventDecorator(content);
  const { image, title, location, slug } = content;

  return (
    <Container>
      {slug && (
        <Hyperlink
          to={`${appDomain}/live/event/${slug}`}
          className="dummy-btn"
          openInNewTab
          aria-label={title}
        />
      )}
      <Artwork src={getArtworkUrl(image) || liveFallback} alt="" />
      <Info>
        <div className="availability">
          <AvailabilityInfo event={event} />
        </div>
        {title && <h3>{title}</h3>}
        {location && <div className="location">{location}</div>}
        {event.shouldShowPrice && (
          <div className="price">
            <PriceTemplate event={event} />
          </div>
        )}
      </Info>
    </Container>
  );
};

LivestreamEvent.propTypes = {
  content: PropTypes.object,
  appDomain: PropTypes.string.isRequired,
};

export default withSiteData(LivestreamEvent);



// WEBPACK FOOTER //
// /tmp/build_83f21ab6/src/components/main/livestreamEvent/LivestreamEvent.js